import * as React from 'react';
import { useCurrentTextStyle } from '../../../../hooks/useCurrentTextStyle';

export const ProductTags = ({ product, globals, isMobile, settings, isClosestMatch }) => {
  const { getCurrentTextStyle } = useCurrentTextStyle();
  const badges = product?.tags || [];
  const collections = product?.collections || [];

  const {
    tag_background_colour,
    tag_text_colour,
    tag_border_colour,
    tag_style,
    tag_style_mobile,
    tag_padding_horizontal,
    tag_padding_vertical,
    tag_padding_horizontal_mobile,
    tag_padding_vertical_mobile,
    tag_radius,
    tag_border_radius,
    tag_border_width,
    tag_layout,
    tag_spacing,
    tag_spacing_mobile,
    tag_configuration,
    collection_configuration,
    max_tags,
    classes,
    padding_top,
    padding_right,
    padding_bottom,
    padding_left,
    margin_top,
    margin_right,
    margin_bottom,
    margin_left,
    flex_direction = 'flex-row',
    flex_wrap = 'flex-wrap',
    gap = 2,
    text_style = 'b1',
    text_style_enable_responsive = false,
    text_style_mobile = 'b1',
    text_style_tablet = 'b1',
    text_style_ultrawide = 'b1'
  } = settings || {};

  // Parse tag configuration from blockSettings
  const parseTagConfiguration = (configText) => {
    if (!configText) return { configMap: {}, configOrder: [] };

    const configMap = {};
    const configOrder: string[] = [];
    const lines = configText.split('\n').filter((line) => line.trim());

    lines.forEach((line) => {
      const trimmedLine = line.trim();
      if (trimmedLine.includes('=')) {
        const [tag, displayText] = trimmedLine.split('=').map((part) => part.trim());
        const lowerTag = tag.toLowerCase();
        configMap[lowerTag] = displayText;
        configOrder.push(lowerTag);
      } else {
        const lowerTag = trimmedLine.toLowerCase();
        configMap[lowerTag] = trimmedLine;
        configOrder.push(lowerTag);
      }
    });

    return { configMap, configOrder };
  };

  // Parse collection configuration from blockSettings
  const parseCollectionConfiguration = (configText) => {
    if (!configText) return { configMap: {}, configOrder: [] };

    const configMap = {};
    const configOrder: string[] = [];
    const lines = configText.split('\n').filter((line) => line.trim());

    lines.forEach((line) => {
      const trimmedLine = line.trim();
      if (trimmedLine.includes('=')) {
        const [handle, displayText] = trimmedLine.split('=').map((part) => part.trim());
        const lowerHandle = handle.toLowerCase();
        configMap[lowerHandle] = displayText;
        configOrder.push(lowerHandle);
      } else {
        const lowerHandle = trimmedLine.toLowerCase();
        configMap[lowerHandle] = trimmedLine;
        configOrder.push(lowerHandle);
      }
    });

    return { configMap, configOrder };
  };

  // Get configured tags and collections
  const tagConfiguration = parseTagConfiguration(tag_configuration);
  const collectionConfiguration = parseCollectionConfiguration(collection_configuration);
  const maxTags = max_tags;

  // Find matching tags in configuration order
  const matchingTags = tagConfiguration.configOrder
    .filter((configTag) => {
      return badges.some((badge) => {
        const lowerBadge = badge.toLowerCase();
        return lowerBadge === configTag || lowerBadge.startsWith(configTag);
      });
    })
    .map((configTag) => tagConfiguration.configMap[configTag]);

  // Find matching collections in configuration order
  const matchingCollections = collectionConfiguration.configOrder
    .filter((configHandle) => {
      return collections.some((collection) => {
        const lowerHandle = collection.handle.toLowerCase();
        return lowerHandle === configHandle;
      });
    })
    .map((configHandle) => collectionConfiguration.configMap[configHandle]);

  // Combine and limit total items
  const allMatchingItems = [...matchingTags, ...matchingCollections].slice(0, maxTags);

  if (allMatchingItems.length === 0 && !isClosestMatch) {
    return null;
  }

  const getTagClasses = () => {
    const baseClasses = `rounded-${tag_radius === 'rounded' ? 'full' : `[${tag_border_radius}px]`}`;
    const paddingHorizontal = isMobile ? tag_padding_horizontal_mobile : tag_padding_horizontal;
    const paddingVertical = isMobile ? tag_padding_vertical_mobile : tag_padding_vertical;
    // const currentTextStyle = getCurrentTextStyle(
    //   text_style,
    //   text_style_enable_responsive,
    //   text_style_mobile,
    //   text_style_tablet,
    //   text_style_ultrawide
    // );

    return `
      ${baseClasses}
      ${isMobile ? tag_style_mobile : tag_style}
      w-fit
      px-[${paddingHorizontal}px] py-[${paddingVertical}px]
    `;
  };

  const getTagStyles = (isClosestMatch?: boolean) => {
    return {
      backgroundColor: isClosestMatch ? "#1F69FF" : tag_background_colour,
      color: isClosestMatch ? "#F5F7F6" : tag_text_colour,
      borderColor: isClosestMatch ? "#1F69FF" : tag_border_colour,
      borderWidth: `${tag_border_width}px`
    };
  };

  const getContainerClasses = () => {
    // Use new flex settings, fallback to legacy tag_layout and tag_spacing
    const direction = flex_direction || tag_layout || 'flex-row';
    const wrap = flex_wrap || 'flex-wrap';
    const spacing = gap !== undefined ? gap : (isMobile ? tag_spacing_mobile : tag_spacing) || 2;

    return `
      flex ${direction} ${wrap} gap-[${spacing}px]
    `;
  };

  const getPaddingMarginClasses = () => {
    return `
      pt-[${padding_top}px]
      pb-[${padding_bottom}px]
      pl-[${padding_left}px]
      pr-[${padding_right}px]
      mt-[${margin_top}px]
      mb-[${margin_bottom}px]
      ml-[${margin_left}px]
      mr-[${margin_right}px]
    `;
  };

  return (
    <>
      <div className={`${getContainerClasses()} ${getPaddingMarginClasses()} ${classes || ''}`}>
        {isClosestMatch && (
          <div
            className={`${getTagClasses()} closest-match-tag`}
            style={getTagStyles(isClosestMatch)}
          >
            Closest Match
          </div>
        )}
        {allMatchingItems.map((itemText, index) => (
          <div
            key={index}
            className={getTagClasses()}
            style={getTagStyles()}
          >
            {itemText}
          </div>
        ))}
      </div>
    </>
  );
};
